import { useState } from "react";
import { CalendarDays, MessageCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { api, waLink } from "@/lib/api";

const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-terra/50";
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function PackageEnquiry({ tour, drone, droneAddon }) {
  const groups = tour.pricing?.length ? tour.pricing : [1, 2, 3, 4, 5, 6].map((n) => ({ label: `${n} person${n > 1 ? "s" : ""}`, price: 0 }));
  const [group, setGroup] = useState(groups[0].label);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const row = groups.find((g) => g.label === group) || groups[0];
  const price = Number(row.price || 0);
  const total = price + (drone && droneAddon ? Number(droneAddon.price) : 0);

  const submit = async () => {
    if (!date) return toast.error("Pick your travel start date");
    if (!name.trim() || !phone.trim()) return toast.error("Add your name and WhatsApp number");
    setBusy(true);
    const droneLine = drone && droneAddon ? ` + ${droneAddon.name} (₹${Number(droneAddon.price).toLocaleString("en-IN")})` : "";
    const priceLine = price ? ` Package price for ${group}: ₹${price.toLocaleString("en-IN")}${droneLine}${droneLine ? ` = ₹${total.toLocaleString("en-IN")}` : ""}.` : "";
    const msg = `Hi Random Rider! I want to book the "${tour.title}" (${tour.duration}) for ${group}, starting ${fmtDate(date)}.${priceLine} Name: ${name}. Please confirm availability.`;
    try {
      await api.post("/enquiries", { name, phone, service: tour.title, travel_dates: fmtDate(date), kind: "package", booking_date: date, estimate: total,
        message: `Package booking: ${tour.title} · ${group} · start ${fmtDate(date)}${priceLine}` });
      toast.success("Request saved — opening WhatsApp to confirm");
    } catch {
      toast.error("Could not save the request, opening WhatsApp anyway");
    } finally {
      setBusy(false);
      window.open(waLink(msg), "_blank");
    }
  };

  return (
    <div className="mt-6 border-t border-ink/10 pt-5" data-testid="package-enquiry">
      <p className="font-mono text-[11px] uppercase tracking-widest text-terra flex items-center gap-1.5"><Users size={12} /> Book this package</p>
      <div className="mt-3 grid grid-cols-1 gap-2.5">
        <label className="text-xs text-ink/60">Group size
          <select value={group} onChange={(e) => setGroup(e.target.value)} className={`${inputCls} mt-1`} data-testid="package-group-select">
            {groups.map((g) => <option key={g.label} value={g.label}>{g.label}{Number(g.price) ? ` — ₹${Number(g.price).toLocaleString("en-IN")}` : ""}</option>)}
          </select>
        </label>
        <label className="text-xs text-ink/60">Travel start date
          <input type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} mt-1`} data-testid="package-date-input" />
        </label>
        <div className="grid grid-cols-2 gap-2.5">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="package-enquiry-name-input" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp number" className={inputCls} data-testid="package-enquiry-phone-input" />
        </div>
      </div>
      {price > 0 && (
        <div className="mt-4 flex items-end justify-between">
          <p className="text-xs font-mono uppercase tracking-widest text-ink/50">Total for {group}</p>
          <p className="font-serif text-3xl font-black text-terra" data-testid="package-enquiry-total">₹{total.toLocaleString("en-IN")}</p>
        </div>
      )}
      <button onClick={submit} disabled={busy} data-testid="package-enquiry-submit"
        className="mt-4 w-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:brightness-110 transition-all disabled:opacity-60">
        {busy ? <CalendarDays size={16} /> : <MessageCircle size={16} />} {busy ? "Saving…" : "Book on WhatsApp"}
      </button>
    </div>
  );
}
